import type { CalendarEvent } from "@shared/schema";

export interface CalendarFilterOptions {
  /**
   * Set of visible local/internal calendar IDs. Local events without an
   * internalCalendarId remain visible for backwards compatibility.
   */
  visibleInternalCalendarIds?: Set<string> | null;
  /**
   * The default GoalVia calendar. Legacy Current Calendar type toggles still
   * apply to this calendar, while custom GoalVia calendars are controlled by
   * their own visibility toggle.
   */
  defaultInternalCalendarId?: string | null;
  /**
   * Set of visible external calendar IDs.
   * If undefined/null, all external calendars are shown (no visibility filter applied).
   * Manual events (without externalCalendarId) are always shown.
   */
  visibleCalendarIds?: Set<string> | null;
  
  /**
   * Filter by event type (event, task, goal).
   * If undefined/null or "all", no type filtering is applied.
   * @default "all"
   */
  eventTypeFilter?: string | null;
  
  /**
   * Filter by event category (multi-select support).
   * Can be a string (legacy single-select) or array (multi-select).
   * If undefined/null, "all", or empty array, no category filtering is applied.
   * If array with values, events matching ANY category are shown (OR logic).
   * @default "all" (legacy) or [] (multi-select)
   */
  categoryFilter?: string | string[] | null;

  /**
   * Client-side quick-toggle hidden sources from the sidebar panel.
   * Keys follow the convention:
   *   "type:event"  — personal/default events (no eventType set)
   *   "type:task"   — task-linked events
   *   "type:goal"   — goal-linked events
   *   "type:reminder" — reminder events
   *   "ext:{id}"    — external calendar by its ExternalCalendar.id UUID
   * If undefined/null, no source filtering is applied.
   */
  hiddenSources?: Set<string> | null;
}

/**
 * Applies calendar filters to events (visibility, type, category, source).
 * Used by both the calendar page and export functionality.
 * 
 * All filters are optional and default to "show all" if not provided.
 * This ensures the helper works correctly even when connections are still loading
 * or filters haven't been set yet.
 */
export function applyCalendarFilters(
  events: CalendarEvent[],
  options: CalendarFilterOptions = {}
): CalendarEvent[] {
  const { 
    visibleInternalCalendarIds,
    defaultInternalCalendarId,
    visibleCalendarIds, 
    eventTypeFilter = "all", 
    categoryFilter = "all",
    hiddenSources,
  } = options;

  // Normalize categoryFilter to array for consistent handling
  const normalizedCategories = Array.isArray(categoryFilter)
    ? categoryFilter
    : categoryFilter === "all" || categoryFilter === null || categoryFilter === undefined
      ? []
      : [categoryFilter];

  return events.filter((event) => {
    // Shared-in events (from another user's calendar share) reference the
    // OWNER's external calendar IDs, which never appear in the viewer's own
    // connections. Their visibility is controlled server-side per share, so
    // they are exempt from the local external-calendar visibility filters.
    const isSharedEvent = (event as any).isSharedEvent === true;

    // Shared-in events are controlled by the share owner and must not be
    // hidden based on the viewer's local calendar list.
    const internalCalendarId = (event as CalendarEvent & { internalCalendarId?: string | null }).internalCalendarId;
    const isCustomInternalEvent = Boolean(
      internalCalendarId &&
      defaultInternalCalendarId &&
      internalCalendarId !== defaultInternalCalendarId,
    );
    if (!isSharedEvent && internalCalendarId && visibleInternalCalendarIds) {
      if (!visibleInternalCalendarIds.has(internalCalendarId)) {
        return false;
      }
    }

    // 1. Visibility filter: Always show manual events
    // Only filter external calendars if visibleCalendarIds is provided
    // A GoalVia calendar assignment remains authoritative after a local event
    // is synced to Google/Outlook. Provider visibility only controls imported
    // provider-only events that have no GoalVia calendar assignment.
    if (!isSharedEvent && !internalCalendarId && event.externalCalendarId && visibleCalendarIds) {
      if (!visibleCalendarIds.has(event.externalCalendarId)) {
        return false;
      }
    }

    // 2. Event type filter (default to "all" if not provided)
    const actualTypeFilter = eventTypeFilter || "all";
    if (actualTypeFilter !== "all" && event.eventType !== actualTypeFilter) {
      return false;
    }

    // 3. Category filter (multi-select with OR logic)
    // Empty array = show all, populated array = show events matching ANY selected category
    if (normalizedCategories.length > 0) {
      if (!event.category || !normalizedCategories.includes(event.category)) {
        return false;
      }
    }

    // 4. Sidebar quick-toggle source filter
    if (!isSharedEvent && !isCustomInternalEvent && hiddenSources && hiddenSources.size > 0) {
      const sourceKey = !internalCalendarId && event.externalCalendarId
        ? `ext:${event.externalCalendarId}`
        : `type:${event.eventType || 'event'}`;
      if (hiddenSources.has(sourceKey)) {
        return false;
      }
    }

    return true;
  });
}
